import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { notifyRequestError } from "@impero-com/common/utils/query_client";
import {
    Button,
    CollapseContentSection,
    emitNotification,
    InformationModal,
    OptionCardGroup,
    Svg,
} from "@impero-com/ui";

import type { ControlPolicy } from "api/generated/model";
import { useGetOrganizationSettingsSegregationOfDutiesConflicts } from "api/generated/organization-settings/organization-settings";
import { ControlPolicyTag } from "components/ControlPolicyTag";
import { Footer } from "components/modals/Footer";
import editPencilSvg from "img/edit-pencil.svg";

import * as theme from "./ControlPolicyCard.css";
import {
    CONTROL_POLICY_OPTIONS,
    type ControlPolicyKind,
    type ControlPolicyOption,
    enforcementChange,
    optionOf,
    updateOf,
} from "./controlPolicyOption";
import {
    type ControlPolicyTexts,
    controlPolicyTexts,
} from "./controlPolicyTexts";
import { useUpdateControlPoliciesMutation } from "./queries/useUpdateControlPoliciesMutation";
import { SegregationOfDutiesConflictsDialog } from "./SegregationOfDutiesConflictsDialog";
import { isSegregationOfDutiesRefusal } from "./segregationOfDutiesRefusal";

interface ControlPolicyCardProps {
    kind: ControlPolicyKind;
    policy: ControlPolicy;
}

export function ControlPolicyCard({ kind, policy }: ControlPolicyCardProps) {
    const { t } = useTranslation();
    const texts = controlPolicyTexts(t, kind);
    const saved = optionOf(policy);
    const [selected, setSelected] = useState<ControlPolicyOption | null>(null);

    return (
        <CollapseContentSection
            styleVariant="primary"
            level={4}
            titleLabel={texts.title}
            subtitleLabel={
                selected === null
                    ? texts.options[saved].summary
                    : texts.description
            }
            collapsed={selected === null}
            hideCollapseButton
            headerRightChildren={
                <div className={theme.headerActions}>
                    <ControlPolicyTag
                        policy={policy}
                        defaultTooltip={t("ControlPolicies_DefaultTooltip")}
                        enforcedTooltip={t("ControlPolicies_EnforcedTooltip")}
                    />
                    {selected === null && (
                        <Button
                            icon={<Svg src={editPencilSvg} />}
                            onClick={() => setSelected(saved)}
                        >
                            {t("ControlPolicies_Edit")}
                        </Button>
                    )}
                </div>
            }
        >
            {selected !== null && (
                <ControlPolicyForm
                    kind={kind}
                    texts={texts}
                    saved={saved}
                    selected={selected}
                    onSelect={setSelected}
                    onDone={() => setSelected(null)}
                />
            )}
        </CollapseContentSection>
    );
}

interface ControlPolicyFormProps {
    kind: ControlPolicyKind;
    texts: ControlPolicyTexts;
    saved: ControlPolicyOption;
    selected: ControlPolicyOption;
    onSelect: (option: ControlPolicyOption) => void;
    onDone: () => void;
}

function ControlPolicyForm({
    kind,
    texts,
    saved,
    selected,
    onSelect,
    onDone,
}: ControlPolicyFormProps) {
    const { t } = useTranslation();
    const mutation = useUpdateControlPoliciesMutation();
    const conflictsQuery =
        useGetOrganizationSettingsSegregationOfDutiesConflicts({
            query: { enabled: false, select: (response) => response.data },
        });
    const [dialog, setDialog] = useState<"confirm" | "conflicts" | null>(null);
    const change = enforcementChange(saved, selected);
    const checksConflicts =
        kind === "segregationOfDuties" && change === "enforce";
    const busy = mutation.isPending || conflictsQuery.isFetching;

    const checkConflicts = () =>
        conflictsQuery.refetch().then(({ data, error }) => {
            if (error) {
                notifyRequestError(error);
                setDialog(null);
            } else {
                setDialog(data?.length ? "conflicts" : "confirm");
            }
        });

    const save = () =>
        mutation.mutate(
            { data: { controlPolicies: updateOf(kind, selected) } },
            {
                onSuccess: () => {
                    setDialog(null);
                    emitNotification(
                        "success",
                        texts.options[selected].savedToast
                    );
                    onDone();
                },
                onError: (error) => {
                    if (isSegregationOfDutiesRefusal(error)) {
                        void checkConflicts();
                    } else {
                        setDialog(null);
                    }
                },
            }
        );

    const saveChanges = () => {
        if (change === null) {
            save();
        } else if (checksConflicts) {
            void checkConflicts();
        } else {
            setDialog("confirm");
        }
    };

    return (
        <div className={theme.form}>
            <OptionCardGroup
                aria-label={texts.title}
                value={selected}
                onChange={(option) => {
                    mutation.reset();
                    onSelect(option);
                }}
                disabled={busy}
                options={CONTROL_POLICY_OPTIONS.map((option) => ({
                    value: option,
                    label: texts.options[option].label,
                    description: texts.options[option].description,
                }))}
            />
            <div className={theme.actions}>
                <Button onClick={onDone} disabled={busy}>
                    {t("Cancel")}
                </Button>
                <Button
                    variant="primary"
                    disabled={selected === saved}
                    loading={dialog === null && busy}
                    onClick={saveChanges}
                >
                    {t("ControlPolicies_SaveChanges")}
                </Button>
            </div>
            <InformationModal
                open={dialog === "confirm"}
                onClose={() => setDialog(null)}
                type="info"
                icon="save"
                title={
                    change === "enforce"
                        ? texts.enforceTitle
                        : texts.unlockTitle
                }
                footer={
                    <Footer
                        onClose={() => setDialog(null)}
                        onConfirm={save}
                        confirmLabel={
                            change === "enforce"
                                ? t("ControlPolicies_EnforcePolicy")
                                : t("ControlPolicies_UnlockPolicy")
                        }
                        loading={busy}
                    />
                }
            >
                {change === "enforce" ? texts.enforceBody : texts.unlockBody}
            </InformationModal>
            {checksConflicts && conflictsQuery.data && (
                <SegregationOfDutiesConflictsDialog
                    open={dialog === "conflicts"}
                    title={texts.enforceTitle}
                    conflicts={conflictsQuery.data}
                    onKeepSetting={() => {
                        setDialog(null);
                        onDone();
                    }}
                />
            )}
        </div>
    );
}
